/**
 * 成果物（sandbox iframe）が渡したページ画像から PDF を組み立てる（親ページ側）。
 *
 * 子が作った PDF をそのまま保存すると、PDF が持てる JavaScript・アクション・添付ファイルを
 * 親が検査できない。そのため子からはページ画像（PNG）だけを受け取り、ブラウザのデコーダで
 * 読み直した画素を JPEG に描き直して、画像を貼るだけの PDF を jsPDF で作る
 * （docs/features/custom-dashboard-security.md）。
 */

/** 用紙（A4）の向き。 */
export type PdfOrientation = 'landscape' | 'portrait';

export const PDF_ORIENTATIONS: readonly PdfOrientation[] = ['landscape', 'portrait'];

/** 1 回の出力の最大ページ数。 */
export const PDF_MAX_PAGES = 100;

/** ページ画像の縦横それぞれの最大ピクセル数。親がデコードするときのメモリを抑える。 */
export const PDF_PAGE_IMAGE_MAX_SIDE_PX = 8192;

const PAGE_MARGIN_MM = 10;
const A4_SHORT_SIDE_MM = 210;
const A4_LONG_SIDE_MM = 297;
const JPEG_QUALITY = 0.85;
const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

/** PDF を作れなかったことを表す。message は閲覧者に見せる理由。 */
export class PdfConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PdfConversionError';
  }
}

/** 余白を除いた A4 の印刷範囲（mm）。子はこの縦横比でページを分ける。 */
export const printableAreaMm = (orientation: PdfOrientation): { width: number; height: number } => {
  const short = A4_SHORT_SIDE_MM - PAGE_MARGIN_MM * 2;
  const long = A4_LONG_SIDE_MM - PAGE_MARGIN_MM * 2;
  return orientation === 'landscape'
    ? { width: long, height: short }
    : { width: short, height: long };
};

/** PNG の IHDR から幅と高さを読む。PNG でなければ null。デコードせずに大きさを検証するために使う。 */
export const readPngSize = (bytes: ArrayBuffer): { width: number; height: number } | null => {
  if (bytes.byteLength < 24) return null;
  const view = new DataView(bytes);
  if (PNG_SIGNATURE.some((byte, index) => view.getUint8(index) !== byte)) return null;
  const chunkType = String.fromCharCode(...new Uint8Array(bytes, 12, 4));
  if (view.getUint32(8) !== 13 || chunkType !== 'IHDR') return null;
  return { width: view.getUint32(16), height: view.getUint32(20) };
};

interface JpegPage {
  jpeg: Uint8Array;
  width: number;
  height: number;
}

const redrawAsJpeg = async (png: ArrayBuffer): Promise<JpegPage> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(new Blob([png], { type: 'image/png' }));
  } catch {
    throw new PdfConversionError('画像として読めないページがあります');
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context) throw new PdfConversionError('ページ画像を描けませんでした');
    // JPEG は透過を持てないため、透過部分を白にする。
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
    });
    if (!blob) throw new PdfConversionError('ページ画像を JPEG にできませんでした');
    return {
      jpeg: new Uint8Array(await blob.arrayBuffer()),
      width: bitmap.width,
      height: bitmap.height,
    };
  } finally {
    bitmap.close();
  }
};

/**
 * 子から受け取ったページ画像（PNG）を 1 枚ずつ A4 の 1 ページに貼った PDF を作る。
 * 画像は縦横比を保って印刷範囲に収め、左上に寄せる。
 * 読めない画像があれば `PdfConversionError` を投げる。
 */
export const convertImagesToPdf = async (
  pages: readonly ArrayBuffer[],
  orientation: PdfOrientation,
): Promise<ArrayBuffer> => {
  // デコード後の画素を同時に抱えないよう 1 枚ずつ描き直す。
  const jpegPages: JpegPage[] = [];
  for (const page of pages) jpegPages.push(await redrawAsJpeg(page));

  // PDF を保存するときだけ読み込む（親の初期表示の bundle に含めない）。
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation });
  const area = printableAreaMm(orientation);
  jpegPages.forEach(({ jpeg, width, height }, index) => {
    if (index > 0) doc.addPage('a4', orientation);
    const scale = Math.min(area.width / width, area.height / height);
    doc.addImage(jpeg, 'JPEG', PAGE_MARGIN_MM, PAGE_MARGIN_MM, width * scale, height * scale);
  });
  return doc.output('arraybuffer');
};
